/*
 * GPU rental profitability (sections/hm-gpu-rent.liquid).
 * Reads the JSON published every hour by the Prefect flow "GPU Rental Profitability" (gpu.happymining.eu) and draws
 * a table (page, collection) or the figures of the product's GPU (product page), recomputed in the browser when the
 * visitor changes the occupancy rate, the electricity price or the number of GPUs.
 * Same formulas as the flow: rent x 24 h x occupancy x owner share - power x hours x electricity price.
 * Page display: a calculator for the visitor's machine (GPU model, number of GPUs, purchase price) and filters on the
 * table (model search, memory, category, "in the shop", sort). Results are an indicative forecast (notice in the page).
 */
(function () {
  'use strict';
  var DAYS_PER_MONTH = 30.4;
  var cache = {};
  // global electricity price of the site (localStorage « hm_kwh », event « hm:kwh », see snippets/hm-rent-config.liquid)
  function kwhUser() { try { var v = parseFloat(window.localStorage.getItem('hm_kwh')); return v >= 0 && v <= 2 ? v : null; } catch (e) { return null; } }
  // default price of the site (window.hmRent.kwh, 0 = no electricity deducted), else the data file's hypothesis
  function kwhBase(a) { var c = window.hmRent && window.hmRent.kwh; return typeof c === 'number' && c >= 0 ? c : ((a && a.kwh_eur) || 0.2); }
  function kwhSave(v, from) {
    if (!(v >= 0 && v <= 2)) return;
    try { window.localStorage.setItem('hm_kwh', String(v)); } catch (e) { /* private mode */ }
    document.dispatchEvent(new CustomEvent('hm:kwh', { detail: { kwh: v, from: from } }));
  }

  function load(url) {
    if (!cache[url]) {
      cache[url] = fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      });
    }
    return cache[url];
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Matching a product title to a tracked GPU model: longest name first, so "RTX 4070 Ti" wins over "RTX 4070".
  function aliases(g) {
    var n = g.name.toUpperCase(), out = [n];
    if (/ SUPER$/.test(n)) out.push(n.replace(/ SUPER$/, 'S'));
    if (g.id === 'a100-pcie') out.push('A100');
    if (g.id === 'h100-sxm') out.push('H100');
    return out;
  }

  function findGpu(gpus, title) {
    var t = ' ' + title.toUpperCase().replace(/NVIDIA|GEFORCE|®|™/g, ' ').replace(/(\d)(TI|SUPER)\b/g, '$1 $2').replace(/[^A-Z0-9]+/g, ' ') + ' ';
    var list = [];
    gpus.forEach(function (g) { aliases(g).forEach(function (a) { list.push([a, g]); }); });
    list.sort(function (a, b) { return b[0].length - a[0].length; });
    for (var i = 0; i < list.length; i++) {
      if (t.indexOf(' ' + list[i][0] + ' ') !== -1) return list[i][1];
    }
    return null;
  }

  // shop products → GPU model (same matching as the product page), the product with the fewest cards first
  function cardCount(title) {
    var m = String(title).match(/(\d{1,2})\s*[x×]\s*/i) || String(title).match(/(\d{1,2})\s*GPU/i);
    return m ? parseInt(m[1], 10) || 1 : 1;
  }
  function gpuProducts(gpus, items) {
    var map = {};
    items.forEach(function (it) {
      var g = findGpu(gpus, it.title);
      if (!g) return;
      var c = cardCount(it.title), cur = map[g.id];
      if (!cur || c < cur.c) map[g.id] = { url: it.url, c: c };
    });
    return map;
  }

  function gpuCount(title) {
    var m = title.match(/(\d{1,2})\s*[x×]\s*(?:nvidia\s+)?(?:geforce\s+)?(?:rtx|a100|h100|h200|l40s|a6000)/i) ||
      title.match(/(\d{1,2})\s*gpu/i);
    var n = m ? parseInt(m[1], 10) : 1;
    return n > 0 && n <= 64 ? n : 1;
  }

  // consumer GeForce cards, professional / workstation cards, data-centre accelerators
  function category(g) {
    var n = g.name.toUpperCase();
    if (/^(A100|A800|H100|H200|H800|B200|B300|GB\d|GH\d|L40S|L40|L4|A40|A10|A30|TESLA|MI\d)/.test(n)) return 'datacenter';
    if (/^(RTX A\d|RTX \d{4} ?ADA|RTX PRO|QUADRO)/.test(n)) return 'pro';
    return 'geforce';
  }

  function month(g, a, util, kwh) {
    var revenue = g.price_h.median * 24 * util * a.host_share * DAYS_PER_MONTH;
    var energy = g.power_w / 1000 * 24 * (util * a.load + (1 - util) * a.idle) * kwh * DAYS_PER_MONTH;
    return { revenue: revenue, power: energy, net: revenue - energy };
  }

  function init(root) {
    var i18n = {};
    try { i18n = JSON.parse(root.querySelector('[data-hm-gpr-i18n]').textContent); } catch (e) { /* labels stay empty */ }
    // Shopify's t filter returns HTML-escaped text (d&#39;achat): decode once, the strings are escaped again on output
    var dec = document.createElement('textarea');
    Object.keys(i18n).forEach(function (k) { dec.innerHTML = i18n[k]; i18n[k] = dec.value; });
    var mode = root.getAttribute('data-mode');
    var locale = root.getAttribute('data-locale') || 'fr';
    var out = root.querySelector('[data-out="body"]');
    var updated = root.querySelector('[data-out="updated"]');
    var form = root.querySelector('.hm-gpr__controls');
    var eur0 = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
    var eur3 = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 3 });
    var eur2 = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var data = null, gpu = null;
    var calc = root.querySelector('.hm-gpr__calc');
    var items = [].slice.call(root.querySelectorAll('[data-hm-gpr-product]')).map(function (li) { return { title: li.getAttribute('data-title'), url: li.getAttribute('data-url') }; });
    var prod = {};
    // « in the shop » = an active product of the shop matches the model (not the flow's own list, which can be stale)
    function inShop(g) { return !!prod[g.id]; }
    var calcOut = root.querySelector('[data-out="calc"]');

    function hourly(v) { return (v < 1 ? eur3 : eur2).format(v); }
    function money(v) { return '<span class="' + (v < 0 ? 'hm-gpr__neg' : 'hm-gpr__pos') + '">' + eur0.format(v) + '</span>'; }

    // filled part of the occupancy slider (CSS --p) and the value read by screen readers
    function fillRange(r) {
      var min = parseFloat(r.min) || 0, max = parseFloat(r.max) || 100;
      r.style.setProperty('--p', ((parseFloat(r.value) - min) / (max - min) * 100) + '%');
      r.setAttribute('aria-valuetext', r.value + ' %');
    }
    form.util.addEventListener('input', function () { fillRange(form.util); root.querySelector('[data-out="util"]').textContent = form.util.value + ' %'; });
    fillRange(form.util);

    function render() {
      if (!data) return;
      var a = data.assumptions || {};
      var util = form.util.value / 100;
      var kwh = parseFloat(String(form.kwh.value).replace(',', '.'));
      if (!(kwh >= 0)) kwh = kwhBase(a);
      root.querySelector('[data-out="util"]').textContent = form.util.value + ' %';
      fillRange(form.util);

      if (mode === 'product') {
        var n = Math.max(1, parseInt(form.count.value, 10) || 1);
        var euro = root.getAttribute('data-currency') === 'EUR';
        out.innerHTML = kpis(gpu, n, euro ? parseFloat(root.getAttribute('data-price')) : 0, a, util, kwh, euro);
        return;
      }
      if (calc && calcOut) {
        var cg = null;
        for (var i = 0; i < data.gpus.length; i++) if (data.gpus[i].id === calc.gpu.value) cg = data.gpus[i];
        var cn = Math.max(1, parseInt(calc.count.value, 10) || 1);
        var cp = parseFloat(String(calc.price.value).replace(',', '.')) || 0;
        calcOut.innerHTML = cg ? kpis(cg, cn, cp, a, util, kwh, true, true) +
          (prod[cg.id] && i18n.see_product ? '<a class="hm-gpr__calc-link" href="' + esc(prod[cg.id].url) + '">' + esc(i18n.see_product) + ' →</a>' : '') : '';
      }

      var rows = data.gpus.slice();
      if (form.shop && form.shop.checked) rows = rows.filter(inShop);
      if (form.q && form.q.value.trim()) {
        var q = form.q.value.trim().toUpperCase().replace(/\s+/g, ' ');
        rows = rows.filter(function (g) { return g.name.toUpperCase().indexOf(q) !== -1; });
      }
      if (form.vram && +form.vram.value) rows = rows.filter(function (g) { return g.vram_gb >= +form.vram.value; });
      if (form.cat && form.cat.value) rows = rows.filter(function (g) { return category(g) === form.cat.value; });
      rows.forEach(function (g) { g._m = month(g, a, util, kwh); });
      // page: best net first (or the chosen sort); collection: the shop's GPUs first, then the other GeForce / RTX cards
      var rtx = function (g) { return /^RTX \d/.test(g.name) ? 1 : 0; };
      var sort = form.sort ? form.sort.value : 'net';
      rows.sort(function (x, y) {
        if (mode === 'collection') return (inShop(y) - inShop(x)) || (rtx(y) - rtx(x)) || y._m.net - x._m.net;
        if (sort === 'vram') return (y.vram_gb - x.vram_gb) || y._m.net - x._m.net;
        if (sort === 'price') return y.price_h.median - x.price_h.median;
        return y._m.net - x._m.net;
      });
      if (mode === 'collection') rows = rows.slice(0, 8);
      if (!rows.length) { out.innerHTML = '<p class="hm-gpr__status">' + esc(i18n.empty) + '</p>'; return; }
      out.innerHTML = (mode === 'page' && i18n.count ? '<p class="hm-gpr__count">' + esc(i18n.count.replace('{n}', rows.length).replace('{total}', data.gpus.length)) + '</p>' : '') +
        '<div class="hm-gpr__table-wrap"><table class="hm-gpr__table"><caption>' + esc(i18n.caption) + '</caption><thead><tr>' +
        '<th scope="col">' + esc(i18n.gpu) + '</th><th scope="col">' + esc(i18n.vram) + '</th><th scope="col">' + esc(i18n.price) +
        '</th><th scope="col">' + esc(i18n.revenue) + '</th><th scope="col">' + esc(i18n.power) + '</th><th scope="col">' + esc(i18n.net) + '</th></tr></thead><tbody>' +
        rows.map(function (g) {
          var nm = prod[g.id] ? '<a href="' + esc(prod[g.id].url) + '"><b>' + esc(g.name) + '</b></a>' : '<b>' + esc(g.name) + '</b>';
          return '<tr><th scope="row">' + nm + (inShop(g) ? '<span class="hm-gpr__badge">' + esc(i18n.in_shop) + '</span>' : '') + '</th>' +
            '<td>' + g.vram_gb + ' ' + esc(i18n.gb) + '</td><td>' + hourly(g.price_h.median) + ' ' + esc(i18n.per_hour) + '</td>' +
            '<td>' + eur0.format(g._m.revenue) + '</td><td>' + eur0.format(g._m.power) + '</td><td><b>' + money(g._m.net) + '</b></td></tr>';
        }).join('') + '</tbody></table></div>';
    }

    // figures of one GPU model for n cards: rent, revenue, electricity, net per month and payback on the price
    function kpis(g, n, price, a, util, kwh, euro, askPrice) {
      var m = month(g, a, util, kwh);
      var net = m.net * n;
      var payback = euro && price > 0 && net > 0 ? Math.ceil(price / net) : null;
      var pb = '';
      if (euro && price > 0) pb = '<div><dt>' + esc(i18n.payback) + '</dt><dd>' + (payback ? payback + ' ' + esc(i18n.months) : '—') + '<small>' + (payback ? eur0.format(price) : esc(i18n.no_payback)) + '</small></dd></div>';
      else if (askPrice) pb = '<div><dt>' + esc(i18n.payback) + '</dt><dd>—<small>' + esc(i18n.calc_price_hint || '') + '</small></dd></div>';
      return '<dl class="hm-gpr__kpis">' +
        '<div><dt>' + esc(i18n.price) + '</dt><dd>' + hourly(g.price_h.median) + '<small>' + esc(i18n.per_hour) + ' · ' + esc(g.name) + '</small></dd></div>' +
        '<div><dt>' + esc(i18n.revenue) + '</dt><dd>' + eur0.format(m.revenue * n) + '<small>' + esc(i18n.per_month) + ' · ' + esc(i18n.for_count.replace('{n}', n)) + '</small></dd></div>' +
        '<div><dt>' + esc(i18n.power) + '</dt><dd>' + eur0.format(m.power * n) + '<small>' + esc(i18n.per_month) + '</small></dd></div>' +
        '<div><dt>' + esc(i18n.net) + '</dt><dd>' + money(net) + '<small>' + esc(i18n.per_month) + ' · ' + eur0.format(m.net) + ' ' + esc(i18n.per_gpu) + '</small></dd></div>' +
        pb + '</dl>';
    }

    load(root.getAttribute('data-url')).then(function (d) {
      if (!d || !d.gpus || !d.gpus.length) throw new Error('empty');
      data = d;
      var a = d.assumptions || {};
      form.util.value = Math.round((a.utilization || 0.6) * 100);
      form.kwh.value = (kwhUser() !== null ? kwhUser() : kwhBase(a)).toFixed(2);
      if (mode === 'product') {
        var title = root.getAttribute('data-title') || '';
        gpu = findGpu(d.gpus, title);
        if (!gpu) return; // model not tracked: the section stays hidden
        form.count.value = gpuCount(title);
        root.hidden = false;
      }
      prod = gpuProducts(d.gpus, items);
      if (calc) {
        // calculator: every tracked model; ?gpu=<id> (links of the home page) or the shop's most profitable GPU first
        var best = null, want = null;
        try { want = new URLSearchParams(location.search).get('gpu'); } catch (e) { /* old browsers */ }
        d.gpus.forEach(function (g) { if (g.id === want) best = g; });
        if (!best) d.gpus.forEach(function (g) { if (inShop(g) && (!best || g.net_month > best.net_month)) best = g; });
        if (!best) best = d.gpus[0];
        if (want && best.id === want) setTimeout(function () { calc.scrollIntoView({ block: 'center' }); }, 50);
        calc.gpu.innerHTML = d.gpus.map(function (g) {
          return '<option value="' + esc(g.id) + '"' + (g === best ? ' selected' : '') + '>' + esc(g.name) + ' · ' + g.vram_gb + ' ' + esc(i18n.gb) + '</option>';
        }).join('');
      }
      if (updated && d.updated_at) {
        var dt = new Date(d.updated_at);
        updated.textContent = i18n.updated.replace('{date}', dt.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }));
      }
      render();
    }).catch(function () {
      if (mode !== 'product') out.innerHTML = '<p class="hm-gpr__status">' + esc(i18n.error) + '</p>';
    });

    form.addEventListener('input', render);
    form.addEventListener('change', render);
    // the visitor's electricity price becomes the price of the whole site, and follows changes made elsewhere
    var me = 'gpr-' + Math.random().toString(36).slice(2);
    form.kwh.addEventListener('change', function () { kwhSave(parseFloat(String(form.kwh.value).replace(',', '.')), me); });
    document.addEventListener('hm:kwh', function (e) {
      if (!data || (e.detail && e.detail.from === me)) return;
      var v = kwhUser();
      form.kwh.value = (v !== null ? v : kwhBase(data.assumptions)).toFixed(2);
      render();
    });
    if (calc) { calc.addEventListener('input', render); calc.addEventListener('change', render); }
  }

  function boot() {
    document.querySelectorAll('[data-hm-gpr]:not([data-ready])').forEach(function (el) {
      el.setAttribute('data-ready', '');
      init(el);
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
